import { Link } from 'react-router-dom'
import { useAuth } from '../../lib/auth'
import { useCapabilities } from '../../lib/capabilities'
import { Card, CardBody, CardHeader, EmptyState } from '../../components/ui'
import {
  AlertIcon, BellIcon, ChartIcon, CheckIcon, CoinIcon, DocIcon, GradCapIcon,
  IdIcon, InboxIcon, RouteIcon, ShieldIcon, UsersGroupIcon, WrenchIcon,
} from '../../components/icons'
import './dashboard.css'

/* Home for operational roles - "just do your functions".
 *
 * The production dashboard (output figures, licences, org-wide activity) is
 * for whoever runs the cooperative: its leadership, the project manager, and
 * the platform administrators. A safety officer, security lead, spotter or
 * HR officer opens the app to DO something, so their home lists exactly the
 * tools their role carries and nothing else - the same capability keys that
 * gate the menu, so a custom role from the Role Builder lands here correctly
 * with no list to hand-maintain. */

interface Tool {
  to: string
  label: string
  body: string
  icon: (props: { size?: number }) => React.ReactElement
  cap?: string
}

const TOOLS: Tool[] = [
  { to: '/safety', label: 'Safety', body: 'Report an incident or a hazard.', icon: AlertIcon },
  { to: '/daily-safety-report', label: 'Daily safety report', body: 'Record the shift’s safety check.', icon: AlertIcon, cap: 'report.daily_safety' },
  { to: '/permits', label: 'Work permits', body: 'Issue and track work permits.', icon: DocIcon, cap: 'permits.manage' },
  { to: '/safety-documents', label: 'Safety documents', body: 'Upload and share safety paperwork.', icon: DocIcon, cap: 'documents.manage' },
  { to: '/gate', label: 'Gate verification', body: 'Check trucks and people at the gate.', icon: ShieldIcon, cap: 'security.gate_verify' },
  { to: '/dispatch', label: 'Loads', body: 'Record a truck load and track it.', icon: RouteIcon, cap: 'dispatch.manage' },
  { to: '/equipment', label: 'Equipment', body: 'Record equipment, for any cooperative.', icon: WrenchIcon, cap: 'dispatch.manage' },
  { to: '/log-expense', label: 'Log expense', body: 'Record money spent in the field.', icon: CoinIcon, cap: 'expense.log' },
  { to: '/sales', label: 'Sales', body: 'Record a sale.', icon: CoinIcon, cap: 'sales.log' },
  { to: '/hr', label: 'Staff records', body: 'Manage people and their documents.', icon: UsersGroupIcon, cap: 'hr.view' },
  { to: '/hr/payroll', label: 'Payroll', body: 'Prepare and review pay.', icon: CoinIcon, cap: 'hr.manage' },
  { to: '/training-manage', label: 'Manage training', body: 'Record sessions and allocate courses.', icon: GradCapIcon, cap: 'training.manage' },
  { to: '/approvals', label: 'Approvals', body: 'Review what needs your decision.', icon: CheckIcon, cap: 'approvals.review' },
  { to: '/reports', label: 'Reports', body: 'Read the reports you have access to.', icon: ChartIcon, cap: 'reports.view.operations' },
  { to: '/learning', label: 'Training', body: 'Take your assigned courses.', icon: GradCapIcon },
  { to: '/kyc', label: 'My documents', body: 'Keep your identity documents current.', icon: IdIcon },
  { to: '/notifications', label: 'Notifications', body: 'Messages and alerts for you.', icon: BellIcon },
]

export default function TasksHome() {
  const { user } = useAuth()
  const caps = useCapabilities()
  const firstName = (user?.full_name || '').split(' ')[0]
  const role = (user?.role ?? '').replace(/_/g, ' ')

  const tools = TOOLS.filter((t) => !t.cap || caps.has(t.cap))

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{firstName ? `Welcome, ${firstName}` : 'Welcome'}</h1>
          <p className="page-head__sub">
            {role ? `Your ${role} tools.` : 'Your tools.'}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader title="What you can do" sub={`${tools.length} tool${tools.length === 1 ? '' : 's'}`} />
        <CardBody>
          {tools.length === 0 ? (
            <EmptyState
              icon={<InboxIcon size={22} />}
              title="Nothing assigned yet"
              body="Once an administrator gives your role its tools, they appear here."
            />
          ) : (
            <div className="grid grid--tiles-2">
              {tools.map(({ to, label, body, icon: Icon }) => (
                <Link key={to} to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <Card hover>
                    <CardBody>
                      <div className="row" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
                        <span style={{ color: 'var(--accent-text)' }}><Icon size={18} /></span>
                        <strong style={{ fontSize: 'var(--text-base)' }}>{label}</strong>
                      </div>
                      <p style={{
                        margin: 'var(--sp-1) 0 0', fontSize: 'var(--text-sm)',
                        color: 'var(--text-muted)',
                      }}>
                        {body}
                      </p>
                    </CardBody>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
